import * as THREE from "three";
import { Board3D } from "./Board3D";
import { CameraRig } from "./CameraRig";
import { Lighting } from "./Lighting";
import type { KnightActor } from "./KnightActor";
import type { Settings } from "../app/Settings";
import { squareToWorld } from "./boardCoordinates";
import { TourPath3D } from "./TourPath3D";
import gsap from "gsap";
import { AquariumEnvironment } from "./AquariumEnvironment";

/**
 * Owns the renderer, scene graph, and the long-lived subsystems (board,
 * lighting, camera rig). One instance for the lifetime of the app; tours
 * come and go, this does not get rebuilt between them.
 */
export class SceneManager {
  readonly scene: THREE.Scene;
  readonly renderer: THREE.WebGLRenderer;
  readonly board: Board3D;
  readonly cameraRig: CameraRig;
  readonly lighting: Lighting;
  readonly tourPath: TourPath3D;
  readonly aquarium: AquariumEnvironment;

  private container: HTMLElement;
  private canvasHost: HTMLDivElement;
  private resizeObserver: ResizeObserver;
  private knight: KnightActor | null = null;
  private startMarker: THREE.Group | null = null;

  constructor(container: HTMLElement, private settings: Settings) {
    this.container = container;
    this.canvasHost = document.createElement("div");
    this.canvasHost.className = "coin-canvas-host";
    container.appendChild(this.canvasHost);

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x05060a);
    this.scene.fog = new THREE.Fog(0x05060a, 14, 26);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.canvasHost.appendChild(this.renderer.domElement);

    this.board = new Board3D(this.scene);
    this.aquarium = new AquariumEnvironment(this.scene, settings.reducedMode);
    this.board.setTransitionMode(settings.themeTransition);
    this.board.setTheme(settings.boardTheme);
    settings.onChange(() => { this.board.setTransitionMode(settings.themeTransition); this.board.setTheme(settings.boardTheme); });
    settings.onChange(() => { if (this.startMarker) this.startMarker.visible = settings.showStartCue; });
    this.tourPath = new TourPath3D(settings);
    this.scene.add(this.tourPath.group);
    this.lighting = new Lighting(this.scene, settings.shadowMapSize);
    this.cameraRig = new CameraRig(this.aspect());
    this.cameraRig.snapTo(new THREE.Vector3(0, 6.5, 8.5), new THREE.Vector3(0, 0, 0));
    this.aquarium.setVisible(settings.sceneMode === "aquarium");
    this.aquarium.setLotusVisible(settings.lotusDecor);
    settings.onChange(() => {this.aquarium.setVisible(settings.sceneMode === "aquarium");this.aquarium.setLotusVisible(settings.lotusDecor);this.aquarium.setFishVisible(settings.smallFish);});

    this.resize();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
  }

  private aspect(): number {
    const width = this.container.clientWidth || 1;
    const height = this.container.clientHeight || 1;
    return width / height;
  }

  resize(): void {
    const width = this.container.clientWidth || 1;
    const height = this.container.clientHeight || 1;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.settings.dprCap));
    this.renderer.setSize(width, height);
    this.cameraRig.updateAspect(width / height);
  }

  setKnight(knight: KnightActor): void {
    if (this.knight) this.scene.remove(this.knight.object);
    this.knight = knight;
    this.scene.add(knight.object);
  }

  getKnight(): KnightActor | null {
    return this.knight;
  }

  squareWorldPosition(square: number, y = 0): THREE.Vector3 {
    return squareToWorld(square, y);
  }

  showLandingRipple(square: number): void {
    if (this.settings.landingEffect === "off") return;
    // Ducks leave a directional wake, created separately with knowledge of
    // the movement vector; a circular impact ring would read as a dive.
    if ((this.settings.performer === "duck-family" || this.settings.performer === "duck") && this.settings.sceneMode === "aquarium") {
      this.aquarium.splash(squareToWorld(square,.12));
      return;
    }
    if (this.settings.landingEffect === "water") {
      const count = this.settings.reducedMode ? 2 : 3;
      for (let i=0;i<count;i++) this.waterRing(square,i*0.09);
      if (this.settings.sceneMode === "aquarium") this.aquarium.splash(squareToWorld(square, .12));
      return;
    }
    const geometry = new THREE.RingGeometry(.13, .18, 28);
    const material = new THREE.MeshBasicMaterial({ color: 0xf2c14e, transparent: true, opacity: .85, side: THREE.DoubleSide, depthWrite: false });
    const ring = new THREE.Mesh(geometry, material);
    ring.rotation.x = -Math.PI / 2;
    ring.position.copy(squareToWorld(square, .075));
    this.scene.add(ring);
    gsap.to(ring.scale, { x: 3.8, y: 3.8, z: 3.8, duration: .65, ease: "power2.out" });
    gsap.to(material, { opacity: 0, duration: .65, ease: "power1.out", onComplete: () => { this.scene.remove(ring); geometry.dispose(); material.dispose(); } });
  }

  private waterRing(square:number, delay:number):void {
    const geometry=new THREE.RingGeometry(.1,.135,40);
    const material=new THREE.MeshBasicMaterial({color:0x70e1df,transparent:true,opacity:.62,side:THREE.DoubleSide,depthWrite:false,blending:THREE.AdditiveBlending});
    const ring=new THREE.Mesh(geometry,material); ring.rotation.x=-Math.PI/2; ring.position.copy(squareToWorld(square,this.settings.sceneMode === "aquarium" ? .735 : .082)); ring.scale.setScalar(.55); this.scene.add(ring);
    gsap.to(ring.scale,{x:6.4,y:6.4,z:6.4,duration:1.15,delay,ease:"power2.out"});
    gsap.to(material,{opacity:0,duration:1.05,delay:delay+.1,ease:"sine.out",onComplete:()=>{this.scene.remove(ring);geometry.dispose();material.dispose()}});
  }

  showTourStart(square: number, continuation: boolean): void {
    if (this.startMarker) {
      this.scene.remove(this.startMarker);
      this.startMarker.traverse(object => {
        const renderable = object as THREE.Object3D & { geometry?: THREE.BufferGeometry; material?: THREE.Material | THREE.Material[] };
        renderable.geometry?.dispose();
        if (Array.isArray(renderable.material)) renderable.material.forEach(material => material.dispose());
        else renderable.material?.dispose();
      });
    }
    const group = new THREE.Group();
    const ringMaterial = new THREE.MeshBasicMaterial({ color: continuation ? 0x65e0c1 : 0xffdf75, transparent: true, opacity: .95, side: THREE.DoubleSide, depthWrite: false });
    const ring = new THREE.Mesh(new THREE.RingGeometry(.27, .34, 36), ringMaterial);
    ring.rotation.x = -Math.PI / 2; ring.position.copy(squareToWorld(square, .09));
    const canvas = document.createElement("canvas"); canvas.width = 320; canvas.height = 72;
    const c = canvas.getContext("2d")!; c.fillStyle = "rgba(5,14,13,.9)"; c.roundRect(2,2,316,68,22); c.fill(); c.strokeStyle = continuation ? "#65e0c1" : "#ffdf75"; c.lineWidth=3; c.stroke(); c.fillStyle="#fff8df"; c.font="700 27px system-ui"; c.textAlign="center"; c.textBaseline="middle"; c.fillText(continuation ? "CONTINUE HERE" : "START HERE",160,37);
    const texture = new THREE.CanvasTexture(canvas);
    const label = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false }));
    label.scale.set(1.55,.35,1); label.position.copy(squareToWorld(square,.72));
    group.add(ring,label); group.visible = this.settings.showStartCue; this.scene.add(group); this.startMarker = group;
    gsap.fromTo(ring.scale, { x: .65, y: .65, z: .65 }, { x: 1.35, y: 1.35, z: 1.35, duration: .7, repeat: 2, yoyo: true, ease: "sine.inOut" });
    gsap.to(label.material, { opacity: .35, delay: 2.8, duration: .8 });
  }

  render(): void {
    this.aquarium.setThreat(this.knight && (this.settings.performer==="duck"||this.settings.performer==="duck-family")?this.knight.object.position:null);
    this.aquarium.update(performance.now());
    this.renderer.render(this.scene, this.cameraRig.camera);
  }

  dispose(): void {
    this.resizeObserver.disconnect();
    this.board.dispose();
    this.tourPath.dispose();
    this.aquarium.dispose();
    this.lighting.dispose(this.scene);
    if (this.knight) this.knight.dispose();
    if (this.startMarker) this.scene.remove(this.startMarker);
    this.renderer.dispose();
    this.canvasHost.remove();
  }
}
